import {
  useFxWorkspaceContext,
  type FxWorkspaceJob,
} from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import { BtnClasses, Button, IconButton } from '@tcweb/connect-ui';
import {
  SelectionTree,
  type TreeNodeDefinition,
} from '@trimble/ui-components/solidjs';
import {
  Component,
  JSX,
  Show,
  batch,
  createMemo,
  createSignal,
} from 'solid-js';

import { CreateDraftLayerDialog } from './CreateDraftLayerDialog';
import { FxWorkspaceEmptyState } from './FxWorkspaceEmptyState';
import {
  buildDraftLayerTreeNodes,
  type DraftLayerTreeItem,
} from './draft-layer-tree-nodes';

const COLLAPSED_GROUP_IDS: string[] = [];

export interface DraftLayersPanelProps {
  readonly workspaceId: string;
}

export const DraftLayersPanel: Component<DraftLayersPanelProps> = (
  props
): JSX.Element => {
  const fxWorkspace = useFxWorkspaceContext();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = createSignal(false);

  const draftLayers = createMemo(() =>
    fxWorkspace.listDraftLayers(props.workspaceId)
  );
  const jobs = createMemo((): readonly FxWorkspaceJob[] => {
    const workspace = fxWorkspace
      .workspaces()
      .find((item) => item.id === props.workspaceId);

    return workspace?.jobs ?? [];
  });
  const breaklinesBuilding = createMemo(() =>
    fxWorkspace.isBreaklinesBuilding(props.workspaceId)
  );
  const nodes = createMemo(() =>
    buildDraftLayerTreeNodes(
      props.workspaceId,
      draftLayers(),
      jobs(),
      breaklinesBuilding()
    )
  );
  const hasContent = createMemo(
    () => draftLayers().length > 0 || jobs().length > 0 || breaklinesBuilding()
  );

  const openCreateDialog = (): void => {
    setIsCreateDialogOpen(true);
  };

  const openNewExtractionDialog = (): void => {
    fxWorkspace.openNewExtractionDialog();
  };

  const isNodeLoading = (): boolean => false;

  const isNodeVisible = (nodeId: string): boolean =>
    fxWorkspace.isExtractedFeatureNodeVisible(nodeId);

  const handleVisibilityToggle = (nodeId: string): void => {
    fxWorkspace.toggleExtractedFeatureNodeVisibility(nodeId);
  };

  const isNodeSelected = (nodeId: string): boolean => {
    const selectedJobId = fxWorkspace.selectedJobId();
    if (
      selectedJobId === undefined ||
      fxWorkspace.selectedWorkspaceId() !== props.workspaceId
    ) {
      return false;
    }

    return nodes().some(
      (node) =>
        node.id === nodeId &&
        node.data?.kind === 'job' &&
        node.data.jobId === selectedJobId
    );
  };

  const handleNodeSelect = (
    _nodeId: string,
    node: TreeNodeDefinition<DraftLayerTreeItem>
  ): void => {
    const data = node.data;
    if (data === undefined || data.kind !== 'job') {
      return;
    }

    batch(() => {
      fxWorkspace.setSelectedWorkspaceId(props.workspaceId);
      fxWorkspace.setSelectedJobId(data.jobId);
    });
  };

  return (
    <div class="flex h-full flex-col" data-testid="workspace-draft-layers">
      <div class="flex items-center justify-between border-b border-base-200 px-2 py-1">
        <span
          class="text-sm font-medium"
          textContent={t('mobileMapping.fxWorkspace.draftLayers.title')}
        />
        <div class="flex items-center gap-1">
          <IconButton
            color={BtnClasses.TERTIARY}
            icon="tc-icon-layers"
            size="icon-medium"
            data-testid="draft-layers-new-extraction-button"
            title={t('mobileMapping.fxWorkspace.extraction.newExtraction')}
            clicked={openNewExtractionDialog}
          />
          <IconButton
            color={BtnClasses.TERTIARY}
            icon="tc-icon-content-add"
            size="icon-medium"
            data-testid="draft-layers-create-button"
            title={t('mobileMapping.fxWorkspace.draftLayers.createCustomLayer')}
            clicked={openCreateDialog}
          />
        </div>
      </div>
      <div class="min-h-0 flex-1 overflow-auto">
        <Show
          when={hasContent()}
          fallback={
            <FxWorkspaceEmptyState
              icon="tc-icon-layers"
              title={t('mobileMapping.fxWorkspace.draftLayers.title')}
              message={t('mobileMapping.fxWorkspace.draftLayers.emptyMessage')}
              dataTestId="draft-layers-empty"
              content={
                <Button
                  color={BtnClasses.PRIMARY}
                  icon="tc-icon-content-add"
                  data-testid="draft-layers-empty-create-button"
                  onClick={openCreateDialog}
                >
                  {t('general.add')}
                </Button>
              }
            />
          }
        >
          <Show when={props.workspaceId} keyed>
            {(_workspaceId) => (
              <SelectionTree
                multiSelect={false}
                childrenLoadStatus="success"
                nodes={nodes()}
                sortBy="label"
                sortDirection="asc"
                defaultExpandIds={COLLAPSED_GROUP_IDS}
                isNodeLoading={isNodeLoading}
                isNodeVisible={isNodeVisible}
                onVisibilityToggle={handleVisibilityToggle}
                isNodeSelected={isNodeSelected}
                onNodeSelect={handleNodeSelect}
              />
            )}
          </Show>
        </Show>
      </div>
      <CreateDraftLayerDialog
        show={isCreateDialogOpen()}
        workspaceId={props.workspaceId}
        onClose={() => setIsCreateDialogOpen(false)}
      />
    </div>
  );
};
